import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _Icon;
var _excluded = ["sx"];
/* eslint-disable max-lines */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Stack from '@mui/material/Stack';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { Button } from '../Button';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { SHADOWS } from '@im/im-theme/constants';
import { useConvertToMobile } from '../utils/useConvertToMobile';
import { Icon } from '../Icon';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var TOAST_MAX_WIDTH = 480;
var TERTIARY_GRAY_BG_COLOR = 'F8FAFC';
var closeButtonHoverBgMap = {
  error: '30',
  information: '30',
  neutral: '',
  primary: '30',
  success: '30',
  warning: '40'
};
export function Toast(props) {
  var _getTypographyColor;
  var controlledAutoHideDuration = props.autoHideDuration,
    _props$actionButtonPr = props.actionButtonProps,
    actionButtonProps = _props$actionButtonPr === void 0 ? {} : _props$actionButtonPr,
    supportingText = props.supportingText,
    actionButtonText = props.actionButtonText,
    _props$compact = props.compact,
    compact = _props$compact === void 0 ? false : _props$compact,
    onActionButtonClick = props.onActionButtonClick,
    onCloseButtonClick = props.onCloseButtonClick,
    header = props.header,
    iconId = props.icon,
    _props$intent = props.intent,
    intent = _props$intent === void 0 ? 'primary' : _props$intent,
    maxWidth = props.maxWidth,
    mobileBreakpoint = props.mobileBreakpoint,
    mobileBreakpointContainerRef = props.mobileBreakpointContainerRef,
    _props$mobilePosition = props.mobilePosition,
    mobilePosition = _props$mobilePosition === void 0 ? 'bottom-center' : _props$mobilePosition,
    open = props.open,
    _props$position = props.position,
    position = _props$position === void 0 ? 'bottom-right' : _props$position,
    sx = props.sx;
  var theme = useTheme();
  var isMobile = useConvertToMobile(mobileBreakpoint, mobileBreakpointContainerRef);
  var shouldCompact = compact || isMobile;
  var positionArray = isMobile ? mobilePosition.split('-') : position.split('-');
  var vertical = positionArray[0] === 'top' ? 'top' : 'bottom';
  var horizontal = positionArray[1];
  var alignItems = iconId && shouldCompact || iconId && supportingText ? 'flex-start' : 'center';
  var isOnlySupportingText = !header && supportingText && !iconId && !onActionButtonClick && !onCloseButtonClick;
  var mobileSpacing = theme.spacing(4);
  var desktopSpacing = theme.spacing(6);
  var spacing = isMobile ? mobileSpacing : desktopSpacing;
  var positionStyleMap = {
    horizontal: {
      center: {
        left: '50%',
        padding: '0 8px',
        transform: 'translateX(-50%)',
        webkitTransform: 'translateX(-50%)',
        width: '100%'
      },
      left: {
        left: spacing,
        right: 'unset'
      },
      right: {
        left: 'unset',
        right: spacing
      }
    },
    vertical: {
      bottom: {
        bottom: spacing,
        top: 'unset'
      },
      center: {},
      top: {
        bottom: 'unset',
        top: spacing
      }
    }
  };
  var getBackgroundAndBorderColor = function getBackgroundAndBorderColor() {
    switch (intent) {
      case 'primary':
        return {
          backgroundColor: theme.palette.gray[950],
          borderColor: theme.palette.gray[950]
        };
      case 'neutral':
        return {
          backgroundColor: theme.palette.common.white,
          borderColor: theme.palette.gray[300]
        };
      case 'information':
        return {
          backgroundColor: theme.palette.primary[700],
          borderColor: theme.palette.primary[700]
        };
      case 'error':
        return {
          backgroundColor: theme.palette.error[700],
          borderColor: theme.palette.error[700]
        };
      case 'warning':
        return {
          backgroundColor: theme.palette.warning[300],
          borderColor: theme.palette.warning[300]
        };
      case 'success':
        return {
          backgroundColor: theme.palette.success[700],
          borderColor: theme.palette.success[700]
        };
    }
  };
  var getTypographyColor = function getTypographyColor() {
    switch (intent) {
      case 'primary':
      case 'information':
      case 'error':
      case 'success':
        return {
          color: theme.palette.common.white
        };
      case 'neutral':
        return {
          color: theme.palette.gray[700]
        };
      case 'warning':
        return {
          color: theme.palette.gray[900]
        };
    }
  };
  var getIconColor = function getIconColor() {
    switch (intent) {
      case 'primary':
      case 'information':
      case 'error':
      case 'success':
        return {
          color: theme.palette.common.white
        };
      case 'neutral':
        return {
          color: theme.palette.gray[700]
        };
      case 'warning':
        return {
          color: theme.palette.gray[700]
        };
    }
  };
  var actionSxProps = actionButtonProps.sx,
    otherActionButtonProps = _objectWithoutProperties(actionButtonProps, _excluded);
  var bodyPaddingL = iconId ? theme.spacing(3) : 0;
  var bodyPaddingR = useMemo(function () {
    if (!onActionButtonClick && !onCloseButtonClick) {
      return 0;
    }
    if (shouldCompact && !onCloseButtonClick) {
      return 0;
    }
    return isMobile ? theme.spacing(8) : theme.spacing(5);
  }, [shouldCompact, isMobile, onActionButtonClick, onCloseButtonClick, theme]);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    iconWidth = _useState2[0],
    setIconWidth = _useState2[1];
  var iconRef = useRef(null);
  useEffect(function () {
    if (iconRef.current) {
      setIconWidth(iconRef.current.offsetWidth);
    }
  }, []);
  var ActionButton = useMemo(function () {
    return onActionButtonClick ? /*#__PURE__*/_jsx(Button, _extends({
      onClick: onActionButtonClick,
      size: "small",
      sx: _extends({
        borderRadius: '32px',
        marginBottom: isMobile ? theme.spacing(2) : 0,
        marginLeft: iconId && shouldCompact ? "calc(".concat(iconWidth, "px + ").concat(bodyPaddingL, ")") : 0,
        marginRight: onCloseButtonClick ? '8px' : 0,
        marginTop: shouldCompact ? theme.spacing(3) : 0,
        padding: '8px 14px',
        textTransform: 'unset'
      }, actionSxProps),
      variant: intent === 'neutral' ? 'secondary' : 'secondary-gray'
    }, otherActionButtonProps, {
      children: actionButtonText
    })) : null;
  }, [actionButtonText, actionSxProps, bodyPaddingL, iconId, iconWidth, intent, isMobile, onActionButtonClick, onCloseButtonClick, otherActionButtonProps, shouldCompact, theme]);
  var CloseButton = useMemo(function () {
    return onCloseButtonClick ? /*#__PURE__*/_jsx(Button, {
      onClick: onCloseButtonClick,
      size: "small",
      sx: {
        '&:hover': {
          backgroundColor: "#".concat(TERTIARY_GRAY_BG_COLOR).concat(closeButtonHoverBgMap[intent])
        }
      },
      variant: "tertiary-gray",
      children: _Icon || (_Icon = /*#__PURE__*/_jsx(Icon, {
        iconId: "XClose"
      }))
    }) : null;
  }, [intent, onCloseButtonClick]);

  // Undefined controlledAutoHideDuration should default to 6 seconds to prevent
  //   affecting existing uses of the component
  var autoHideDuration = controlledAutoHideDuration === undefined ? 6000 : controlledAutoHideDuration;
  var getSupportingTextBottomPadding = function getSupportingTextBottomPadding() {
    if (!onActionButtonClick || !shouldCompact || !header) return theme.spacing(2);
    return 0;
  };
  return /*#__PURE__*/_jsx(Snackbar, {
    anchorOrigin: {
      horizontal: horizontal,
      vertical: vertical
    },
    autoHideDuration: autoHideDuration,
    onClose: onCloseButtonClick,
    open: open,
    sx: _extends({
      width: 'fit-content'
    }, positionStyleMap.horizontal[isMobile ? 'center' : horizontal], positionStyleMap.vertical[vertical], sx),
    "data-element": "Snackbar",
    "data-component": "Toast",
    "data-source-file": "Toast.tsx",
    children: /*#__PURE__*/_jsxs(Alert, {
      icon: false,
      sx: _extends({
        '.MuiAlert-icon': {
          alignItems: alignItems
        },
        '.MuiAlert-message': _extends({}, isOnlySupportingText && {
          alignItems: 'center',
          display: 'flex'
        }, {
          padding: 0,
          width: '100%'
        }),
        '.MuiSvgIcon-root': _extends({}, getIconColor()),
        borderRadius: '12px',
        boxShadow: theme.shadows[SHADOWS.xs],
        gap: '16px',
        maxWidth: maxWidth != null ? maxWidth : TOAST_MAX_WIDTH,
        minHeight: '60px',
        padding: isMobile ? "".concat(theme.spacing(2), " ").concat(theme.spacing(4)) : "".concat(theme.spacing(3), " ").concat(theme.spacing(4)),
        width: '100%'
      }, getBackgroundAndBorderColor()),
      variant: "filled",
      "data-element": "Alert",
      "data-source-file": "Toast.tsx",
      children: [/*#__PURE__*/_jsxs(Stack, {
        direction: "row",
        sx: _extends({
          alignItems: alignItems,
          width: '100%'
        }, !header && supportingText && !isMobile && !compact && {
          alignItems: 'center'
        }),
        "data-element": "Stack",
        "data-source-file": "Toast.tsx",
        children: [iconId && /*#__PURE__*/_jsx(Icon, {
          iconId: iconId,
          ref: iconRef,
          size: 20,
          sx: {
            mt: supportingText && header || shouldCompact && !supportingText || shouldCompact ? 2 : 0
          }
        }), /*#__PURE__*/_jsxs(Stack, {
          spacing: 1,
          sx: {
            flex: 1,
            margin: "0 ".concat(bodyPaddingR, " 0 ").concat(bodyPaddingL, "}"),
            position: 'relative'
          },
          "data-element": "Stack",
          "data-source-file": "Toast.tsx",
          children: [header && /*#__PURE__*/_jsx(Typography, {
            className: "header-message",
            sx: _extends({}, getTypographyColor(), {
              pb: !supportingText ? theme.spacing(2) : 0,
              pt: theme.spacing(2)
            }),
            variant: "text-sm-semibold",
            children: header
          }), supportingText && /*#__PURE__*/_jsx(Typography, {
            className: "body-message",
            sx: {
              color: intent === 'neutral' ? theme.palette.gray[600] : (_getTypographyColor = getTypographyColor()) == null ? void 0 : _getTypographyColor.color,
              pb: getSupportingTextBottomPadding(),
              pt: header ? 0 : theme.spacing(2)
            },
            variant: "text-sm-regular",
            children: supportingText
          })]
        }), !shouldCompact && ActionButton, CloseButton]
      }), shouldCompact && ActionButton]
    })
  });
}